import { useSnackbar } from 'notistack';
import { JSX, ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppErrorHandler } from '.';
import { ErrorPage } from '../../pages/error';
import { ErrorDialog } from './error-dialog';
import {
  APP_ERROR_TYPE,
  AppErrorType,
  ERROR_USER_INTERFACE,
  ErrorUserInterfaceType,
} from './types';

interface Props {
  children: ReactNode;
  errorUserInterfaceType?: ErrorUserInterfaceType;
}

type ErrorInfo = {
  title: string;
  description: string;
};

export function AppErrorHandler({
  children,
  errorUserInterfaceType = ERROR_USER_INTERFACE.DIALOG,
}: Props): JSX.Element | ReactNode {
  const { t } = useTranslation(['common', 'issue']);
  const { error, setError } = useAppErrorHandler();
  const { enqueueSnackbar } = useSnackbar();

  const hasHomeButton = useMemo(() => {
    if (!error?.type) return false;

    const homeNavigationErrorTypes: AppErrorType[] = [
      APP_ERROR_TYPE.UNAUTHORIZED,
      APP_ERROR_TYPE.UNAUTHORIZED_TO_VIEW_ISSUE,
      APP_ERROR_TYPE.UNAUTHORIZED_TO_MANAGE_ISSUE,
      APP_ERROR_TYPE.FORBIDDEN,
      APP_ERROR_TYPE.NOT_FOUND,
      APP_ERROR_TYPE.ISSUE_NOT_FOUND,
    ];

    return homeNavigationErrorTypes.includes(error.type);
  }, [error?.type]);

  const hasReloadButton = useMemo(
    () => error?.type === APP_ERROR_TYPE.NETWORK,
    [error?.type],
  );

  const hasBackButton = useMemo(() => {
    if (!error?.type) return false;

    const backNavigationErrorTypes: AppErrorType[] = [
      APP_ERROR_TYPE.INVALID_SOURCE_PARAMS,
    ];

    return backNavigationErrorTypes.includes(error.type);
  }, [error?.type]);

  const errorTranslations: Record<AppErrorType, ErrorInfo> = useMemo(
    () => ({
      badRequest: {
        title: t('common:errors.badRequestError.title'),
        description: t('common:errors.badRequestError.description'),
      },
      forbidden: {
        title: t('common:errors.forbiddenError.title'),
        description: t('common:errors.forbiddenError.description'),
      },
      invalidSourceParams: {
        title: t('common:errors.invalidSourceParams.title'),
        description: t('common:errors.invalidSourceParams.description'),
      },
      issueNotFound: {
        title: t('common:errors.notFound.title'),
        description: t('common:errors.notFound.description'),
      },
      network: {
        title: t('common:errors.general.title'),
        description: t('common:errors.general.description'),
      },
      notFound: {
        title: t('common:errors.notFound.title'),
        description: t('common:errors.notFound.description'),
      },
      scanningFile: {
        title: t('common:errors.scanningFile.title'),
        description: t('common:errors.scanningFile.description'),
      },
      unauthorized: {
        title: t('common:errors.unauthorizedError.title'),
        description: t('common:errors.unauthorizedError.description'),
      },
      unauthorizedToViewIssue: {
        title: t('common:errors.notFound.title'),
        description: t('common:errors.notFound.description'),
      },
      unauthorizedToManageIssue: {
        title: t('common:errors.notFound.title'),
        description: t('common:errors.notFound.description'),
      },
      userCannotUpdateAction: {
        title: t('common:errors.userCannotUpdateAction.title'),
        description: t('common:errors.userCannotUpdateAction.description'),
      },
      actionCannotBeApproved: {
        title: t('common:errors.actionCannotBeApproved.title'),
        description: t('common:errors.actionCannotBeApproved.description'),
      },
      actionCannotBeDeclined: {
        title: t('common:errors.actionCannotBeDeclined.title'),
        description: t('common:errors.actionCannotBeDeclined.description'),
      },
      actionCannotBeFinished: {
        title: t('common:errors.actionCannotBeFinished.title'),
        description: t('common:errors.actionCannotBeFinished.description'),
      },
      actionCannotBeStarted: {
        title: t('common:errors.actionCannotBeStarted.title'),
        description: t('common:errors.actionCannotBeStarted.description'),
      },
      IssueSubtypeHasLinkedIssueTypeManagersError: {
        title: t('common:errors.badRequestError.title'),
        description: t(
          'common:errors.issueSubtypeHasLinkedIssueTypeManagersError',
        ),
      },
      issueSubtypeHasIssueManagerOfIssueType: {
        title: t('common:errors.badRequestError.title'),
        description: t(
          'common:errors.issueSubtypeHasIssueTypeManagerOfIssueType',
        ),
      },
      issueTypeHasIssueManagerOfIssueSubtypes: {
        title: t('common:errors.badRequestError.title'),
        description: t(
          'common:errors.issueTypeHasIssueTypeManagerOfIssueSubtypes',
        ),
      },
      issueSubtypeAlreadyExistsError: {
        title: t('common:errors.badRequestError.title'),
        description: t('common:errors.issueSubtypeAlreadyExistsError'),
      },
      issueTemplateAlreadyExists: {
        title: t('common:errors.badRequestError.title'),
        description: t('common:errors.issueTemplateAlreadyExists'),
      },
      slaAlreadyExistsError: {
        title: t('common:errors.badRequestError.title'),
        description: t('common:errors.slaAlreadyExistsError'),
      },
      issueTemplateDueDateIsGreaterThanSLA: {
        title: t('common:errors.badRequestError.title'),
        description: t('common:errors.issueTemplateDueDateIsGreaterThanSLA'),
      },
      actionDueDateMustBeLessOrEqualThanIssueTemplateDueDate: {
        title: t('common:errors.badRequestError.title'),
        description: t(
          'common:errors.actionDueDateMustBeLessOrEqualThanIssueTemplateDueDate',
        ),
      },
      mutationError: {
        title: t('common:errors.mutationError.title'),
        description: t('common:errors.mutationError.description'),
      },
      issueAlreadyClosedError: {
        title: t('issue:view.sla.errorMessages.issueAlreadyClosedError'),
        description: t('issue:view.sla.errorMessages.issueAlreadyClosedError'),
      },
      issueSLAAlreadyDisabledError: {
        title: t(
          'issue:view.sla.errorMessages.issueSLAAlreadyDeactivatedError',
        ),
        description: t(
          'issue:view.sla.errorMessages.issueSLAAlreadyDeactivatedError',
        ),
      },
      issueSLAAlreadyEnabledError: {
        title: t('issue:view.sla.errorMessages.issueSLAAlreadyActivatedError'),
        description: t(
          'issue:view.sla.errorMessages.issueSLAAlreadyActivatedError',
        ),
      },
      invalidIssueTypeSubtype: {
        title: t('issue:errors.invalidIssueTypeSubtype'),
        description: t('issue:errors.invalidIssueTypeSubtype'),
      },
      maintenanceModeError: {
        title: t('common:errors.maintenanceModeError.title'),
        description: t('common:errors.maintenanceModeError.description'),
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Handle toast notifications for permission-related errors
  useEffect(() => {
    if (error && error.userInterface === ERROR_USER_INTERFACE.TOAST) {
      const errorTranslation = errorTranslations[error.type];

      enqueueSnackbar(errorTranslation.description, {
        variant: 'error',
      });
      setError(null);
    }
  }, [error, enqueueSnackbar, setError, errorTranslations]);

  if (!error || errorUserInterfaceType === ERROR_USER_INTERFACE.TOAST) {
    return children;
  }

  const errorTranslation = errorTranslations[error.type];
  if (errorUserInterfaceType === ERROR_USER_INTERFACE.PAGE) {
    return (
      <ErrorPage
        title={errorTranslation.title}
        description={errorTranslation.description}
        hasHomeButton={hasHomeButton}
        hasReloadButton={hasReloadButton}
      />
    );
  }
  if (errorUserInterfaceType === ERROR_USER_INTERFACE.DIALOG) {
    return (
      <>
        {children}
        <ErrorDialog
          title={errorTranslation.title}
          description={errorTranslation.description}
          hasHomeButton={hasHomeButton}
          hasBackButton={hasBackButton}
          isOpen={!!error}
          handleClose={() => setError(null)}
        />
      </>
    );
  }
}
